import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Text } from 'grommet';
import { MetricCard } from '@Components/Partial/Product/Analytics';
import {
  StyledPanel,
  StyledScrollWrap,
  StyledTable,
} from './StyledCidAnalyticsDashboardPage';
import {
  REVENUE_COLORS,
  formatMoney, formatMonthLabel,
  PanelTitle, ColorDot, EmptyTable,
} from './CidAnalyticsShared';
import { RevenueAreaChart, CreatorGrowthChart } from './CidAnalyticsCharts';

export default function CidAnalyticsOverviewTab({
  overview,
  revenueTotals,
  revenueByMonth,
  revenueRanks,
  revenuePerCreator,
  videoStats,
  creatorGrowth,
  loading,
  page,
  small,
  hasFilters,
}) {
  return (
    <Box gap="1rem">

      {/* KPI Cards */}
      <Grid columns={small ? ['1fr', '1fr'] : ['1fr', '1fr', '1fr', '1fr', '1fr']} gap="1rem">
        <MetricCard
          label="TOTAL CREATORS"
          value={overview ? String(overview.totalCreators) : 'Data unavailable'}
          subLabel="active creators on the platform"
          textColor={page.primaryText}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
        />
        <MetricCard
          label="OFFBOARDED CREATORS"
          value={overview ? String(overview.totalOffboardedCreators) : 'Data unavailable'}
          subLabel="deactivated or disqualified creators"
          textColor={page.primaryText}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
        />
        <MetricCard
          label="ACTIVE CLAIMS"
          value={overview ? overview.totalActiveClaims.toLocaleString() : 'Data unavailable'}
          subLabel="videos claimed & actively monetizing"
          textColor={page.primaryText}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
        />
        <MetricCard
          label="REFERENCE VIDEOS"
          value={overview ? overview.totalReferenceVideos.toLocaleString() : 'Data unavailable'}
          subLabel="reference files uploaded to Content ID"
          textColor={page.primaryText}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
        />
        <MetricCard
          label="EST. PARTNER REVENUE"
          value={overview ? formatMoney(overview.totalEstimatedPartnerRevenue) : 'Data unavailable'}
          subLabel={hasFilters ? 'from revenue reports — selected period' : 'from revenue reports — all time'}
          textColor={page.primaryText}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={page.highlight}
        />
      </Grid>

      {/* Revenue Summary Cards */}
      <Grid columns={small ? ['1fr'] : ['1fr', '1fr', '1fr']} gap="1rem">
        <MetricCard
          label="TOTAL PARTNER REVENUE"
          value={revenueTotals ? formatMoney(revenueTotals.totalPartnerRevenue) : 'Data unavailable'}
          subLabel={hasFilters ? 'selected period' : 'all time'}
          textColor={REVENUE_COLORS.totalPartnerRevenue}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={REVENUE_COLORS.totalPartnerRevenue}
        />
        <MetricCard
          label="TOTAL REDFLAG REVENUE"
          value={revenueTotals ? formatMoney(revenueTotals.redflagShare) : 'Data unavailable'}
          subLabel={hasFilters ? 'selected period' : 'all time'}
          textColor={REVENUE_COLORS.redflagShare}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={REVENUE_COLORS.redflagShare}
        />
        <MetricCard
          label="TOTAL PAYABLE REVENUE"
          value={revenueTotals ? formatMoney(revenueTotals.payableRevenue) : 'Data unavailable'}
          subLabel={hasFilters ? 'selected period' : 'all time'}
          textColor={REVENUE_COLORS.payableRevenue}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={REVENUE_COLORS.payableRevenue}
        />
      </Grid>

      {/* Revenue Growth Area Chart */}
      <StyledPanel gap="0.75rem">
        <PanelTitle label="Revenue Growth per Month" />
        <Box direction="row" gap="1rem" align="center" flex={false}>
          <Box direction="row" align="center" gap="0.35rem">
            <ColorDot color={REVENUE_COLORS.totalPartnerRevenue} />
            <Text size="0.75rem" color={page.secondaryText}>Partner Revenue</Text>
          </Box>
          <Box direction="row" align="center" gap="0.35rem">
            <ColorDot color={REVENUE_COLORS.redflagShare} />
            <Text size="0.75rem" color={page.secondaryText}>Redflag Earnings</Text>
          </Box>
          <Box direction="row" align="center" gap="0.35rem">
            <ColorDot color={REVENUE_COLORS.payableRevenue} />
            <Text size="0.75rem" color={page.secondaryText}>Payable Revenue</Text>
          </Box>
        </Box>
        <RevenueAreaChart data={revenueByMonth} loading={loading} />
      </StyledPanel>

      {/* Two-column: Creator Onboarding + Revenue by Month */}
      <Grid columns={small ? ['100%'] : ['1fr', '1fr']} gap="1rem">
        <StyledPanel gap="0.75rem">
          <PanelTitle label="Creators Onboarded per Month" />
          <CreatorGrowthChart data={creatorGrowth} loading={loading} />
        </StyledPanel>

        <StyledPanel gap="0.75rem">
          <PanelTitle label="Revenue by Month" />
          {revenueByMonth && revenueByMonth.length > 0 ? (
            <StyledScrollWrap>
              <StyledTable>
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Partner Rev.</th>
                    <th>Redflag</th>
                    <th>Payable</th>
                    <th>Creators</th>
                  </tr>
                </thead>
                <tbody>
                  {revenueByMonth.map((row) => (
                    <tr key={row.month}>
                      <td>{formatMonthLabel(row.month)}</td>
                      <td>{formatMoney(row.totalPartnerRevenue)}</td>
                      <td>{formatMoney(row.redflagShare)}</td>
                      <td>{formatMoney(row.payableRevenue)}</td>
                      <td>{row.creatorCount}</td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </StyledScrollWrap>
          ) : <EmptyTable page={page} loading={loading} />}
        </StyledPanel>
      </Grid>

      {/* Revenue Rankings Table */}
      <StyledPanel gap="0.75rem">
        <PanelTitle label="Revenue Rankings by Month" />
        {revenueRanks && revenueRanks.length > 0 ? (
          <StyledScrollWrap>
            <StyledTable>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Month</th>
                  <th style={{ textAlign: 'left' }}>Highest Creator</th>
                  <th>Highest Rev.</th>
                  <th style={{ textAlign: 'left' }}>Lowest Creator</th>
                  <th>Lowest Rev.</th>
                  <th>Avg. Rev.</th>
                  <th>Creators</th>
                </tr>
              </thead>
              <tbody>
                {revenueRanks.map((row) => (
                  <tr key={row.month}>
                    <td style={{ textAlign: 'left' }}>{formatMonthLabel(row.month)}</td>
                    <td style={{
                      textAlign: 'left', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                    >
                      {row.highestCreator || '—'}
                    </td>
                    <td style={{ color: '#10b981', fontWeight: 600 }}>{formatMoney(row.highestRevenue)}</td>
                    <td style={{
                      textAlign: 'left', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                    >
                      {row.lowestCreator || '—'}
                    </td>
                    <td style={{ color: '#ef4444', fontWeight: 600 }}>{formatMoney(row.lowestRevenue)}</td>
                    <td>{formatMoney(row.averageRevenue)}</td>
                    <td>{row.creatorCount}</td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </StyledScrollWrap>
        ) : <EmptyTable page={page} loading={loading} />}
      </StyledPanel>

      {/* Revenue per Creator */}
      <StyledPanel gap="0.75rem">
        <PanelTitle label="Revenue per Creator" />
        {revenuePerCreator && revenuePerCreator.length > 0 ? (
          <StyledScrollWrap>
            <StyledTable>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', width: '40px' }}>#</th>
                  <th style={{ textAlign: 'left' }}>Creator</th>
                  <th>Partner Rev.</th>
                  <th>Redflag</th>
                  <th>Payable</th>
                </tr>
              </thead>
              <tbody>
                {revenuePerCreator.map((row, i) => (
                  <tr key={row.assetLabel}>
                    <td style={{ textAlign: 'left', color: 'var(--page-hint-text)' }}>{i + 1}</td>
                    <td style={{ textAlign: 'left' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                        {row.customerName || row.assetLabel}
                        <span style={{
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          padding: '1px 5px',
                          borderRadius: '3px',
                          background: row.isActive ? '#16a34a' : '#6b7280',
                          color: '#fff',
                          letterSpacing: '0.02em',
                          whiteSpace: 'nowrap',
                        }}
                        >
                          {row.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </span>
                    </td>
                    <td style={{ color: REVENUE_COLORS.totalPartnerRevenue, fontWeight: 600 }}>
                      {formatMoney(row.totalPartnerRevenue)}
                    </td>
                    <td style={{ color: REVENUE_COLORS.redflagShare }}>
                      {formatMoney(row.redflagShare)}
                    </td>
                    <td style={{ color: REVENUE_COLORS.payableRevenue }}>
                      {formatMoney(row.payableRevenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </StyledScrollWrap>
        ) : <EmptyTable page={page} loading={loading} />}
      </StyledPanel>

      {/* Reference Videos per Creator */}
      <StyledPanel gap="0.75rem">
        <PanelTitle label="Reference Videos per Creator" />
        {videoStats && videoStats.length > 0 ? (
          <StyledScrollWrap>
            <StyledTable>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', width: '40px' }}>#</th>
                  <th style={{ textAlign: 'left' }}>Asset Label</th>
                  <th>Reference Videos</th>
                </tr>
              </thead>
              <tbody>
                {videoStats.map((row, i) => (
                  <tr key={row.assetLabel}>
                    <td style={{ textAlign: 'left', color: 'var(--page-hint-text)' }}>{i + 1}</td>
                    <td style={{ textAlign: 'left' }}>{row.assetLabel}</td>
                    <td style={{ fontWeight: 600 }}>{row.referenceVideoCount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </StyledScrollWrap>
        ) : <EmptyTable page={page} loading={loading} />}
      </StyledPanel>

    </Box>
  );
}

CidAnalyticsOverviewTab.propTypes = {
  overview: PropTypes.shape({
    totalCreators: PropTypes.number,
    totalOffboardedCreators: PropTypes.number,
    totalActiveClaims: PropTypes.number,
    totalReferenceVideos: PropTypes.number,
    totalEstimatedPartnerRevenue: PropTypes.number,
  }),
  revenueTotals: PropTypes.shape({
    totalPartnerRevenue: PropTypes.number,
    redflagShare: PropTypes.number,
    payableRevenue: PropTypes.number,
  }),
  revenueByMonth: PropTypes.array,
  revenueRanks: PropTypes.array,
  revenuePerCreator: PropTypes.array,
  videoStats: PropTypes.array,
  creatorGrowth: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  page: PropTypes.object.isRequired,
  small: PropTypes.bool.isRequired,
  hasFilters: PropTypes.bool.isRequired,
};
